/**
 * The session, surviving the browser.
 *
 * A training shift is measured in days, and a browser tab is not. Before this
 * file, F5 was a fab fire: the journal - the record a student is assessed on -
 * the lot in the carrier, and every panel's recipe vanished together. The
 * journal was already an event log, which is the correct substrate for
 * persistence; it had just never been written anywhere.
 *
 * WHAT IS SAVED, AND WHY EXACTLY THIS
 *
 *   journal   every event, capped at the newest MAX_EVENTS. The record IS the
 *             student's work product; losing it loses the debrief.
 *   carrier   the wafer, films and accounts and all. A lot three tools into a
 *             route is hours of walking; it does not evaporate at midnight in
 *             a real fab either.
 *   panels    whatever each panel opts into via save()/load() - typically its
 *             recipe and tool condition, so a detuned tool is still detuned
 *             tomorrow. Opt-in, because a panel knows which of its fields are
 *             state and which are cache.
 *
 * WHAT IS DELIBERATELY NOT SAVED: anything derived. Chart pixels, solver
 * results, assistant transcripts - all recomputable or conversational. A save
 * format full of derived state is a save format that lies after a code change.
 *
 * Storage is localStorage behind try/catch everywhere: private windows, group
 * policies and full disks all make it throw, and a training tool that crashes
 * on save is strictly worse than one that merely forgets. Sessions expire
 * after MAX_AGE_DAYS so a lab machine's shared profile does not resurrect one
 * student's half-shift under the next student's login.
 */

import { Journal, type JournalEvent } from './Journal'
import type { Carrier, FilmLayer, Wafer } from '../sim/wafer/model'

const KEY = 'simfab.session.v1'
const MAX_EVENTS = 4000
const MAX_AGE_DAYS = 14
const SAVE_DEBOUNCE_MS = 800

/** A wafer with its typed arrays flattened for JSON. */
interface StoredWafer extends Omit<Wafer, 'x' | 'stack'> {
  x: number[]
  stack: Array<Omit<FilmLayer, 'thicknessNm'> & { thicknessNm: number[] }>
}

interface StoredSession {
  savedAt: number
  events: JournalEvent[]
  wafer: StoredWafer | null
  panels: Record<string, unknown>
}

const serialiseWafer = (w: Wafer): StoredWafer => ({
  ...w,
  x: Array.from(w.x),
  stack: w.stack.map((l) => ({ ...l, thicknessNm: Array.from(l.thicknessNm) })),
})

const reviveWafer = (s: StoredWafer): Wafer => ({
  ...s,
  x: Float64Array.from(s.x),
  stack: s.stack.map((l) => ({ ...l, thicknessNm: Float64Array.from(l.thicknessNm) })),
})

/** Panel blobs from the restored session, handed out as panels are built. */
let restoredPanels: Record<string, unknown> = {}

/**
 * The stored state for one panel, or undefined.
 *
 * Called by the registry at construction time, which is the only moment a
 * panel can be seeded cheaply - panels are built lazily and live forever, so
 * there is exactly one such moment per panel per session.
 */
export const storedPanelState = (id: string): unknown => restoredPanels[id]

export interface SessionHooks {
  journal: Journal
  carrier: Carrier
  /** Every built panel's saved state, harvested at save time. */
  collectPanelStates(): Record<string, unknown>
}

/**
 * Restore what a previous session left, then keep the current one written.
 *
 * Call once at startup, after the journal and carrier exist and before any
 * panel is built. Returns true when something was actually restored, so the
 * caller can tell a resumed shift from a fresh one.
 */
export function attachSession(hooks: SessionHooks): boolean {
  let restored = false
  try {
    const raw = localStorage.getItem(KEY)
    if (raw) {
      const s = JSON.parse(raw) as StoredSession
      const fresh = Date.now() - s.savedAt < MAX_AGE_DAYS * 24 * 3600 * 1000
      if (fresh && Array.isArray(s.events)) {
        if (s.events.length > 0) hooks.journal.restore(s.events)
        if (s.wafer) hooks.carrier.set(reviveWafer(s.wafer))
        restoredPanels = s.panels ?? {}
        restored = s.events.length > 0 || s.wafer !== null
      } else if (!fresh) {
        localStorage.removeItem(KEY)
      }
    }
  } catch {
    // A corrupt or unreadable store is a fresh session, not a crash.
    restoredPanels = {}
  }

  let timer: ReturnType<typeof setTimeout> | undefined
  const save = () => {
    try {
      const wafer = hooks.carrier.current
      const session: StoredSession = {
        savedAt: Date.now(),
        events: hooks.journal.events.slice(-MAX_EVENTS),
        wafer: wafer ? serialiseWafer(wafer) : null,
        // Keep restored blobs for panels not yet rebuilt this session - a
        // panel the student has not reopened has not forgotten anything.
        panels: { ...restoredPanels, ...hooks.collectPanelStates() },
      }
      localStorage.setItem(KEY, JSON.stringify(session))
    } catch {
      // Nothing to do: no storage means no persistence, and that is all.
    }
  }
  const scheduleSave = () => {
    clearTimeout(timer)
    timer = setTimeout(save, SAVE_DEBOUNCE_MS)
  }

  hooks.journal.subscribe(scheduleSave)
  hooks.carrier.subscribe(scheduleSave)
  // The debounce loses the tail of a session ending mid-timer; pagehide is
  // the last reliable moment to flush it. 'beforeunload' is not fired on
  // mobile and 'unload' kills bfcache, so pagehide is the one to use.
  try {
    addEventListener('pagehide', save)
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') save()
    })
  } catch {
    // Non-browser context: harness imports never get here.
  }

  return restored
}

/** Forget the stored session. The in-memory one is untouched. */
export function clearStoredSession(): void {
  try {
    localStorage.removeItem(KEY)
  } catch {
    // Already gone as far as anyone can tell.
  }
}
